import { clientLogger } from '@/lib/clientLogger';
import type { QueryClient } from '@tanstack/react-query';
import { apiService } from './api';
import { getStorageItem, setStorageItem, removeStorageItem } from './capacitor';
import { queryPersister } from './queryPersister';

const SESSION_STORAGE_KEYS = [
    'selected_org_id',
    'selectedCompanyData',
    'stripe_oauth_company',
] as const;

// Legacy keys replaced by `selectedCompanyData` — cleared during migration and on logout
const LEGACY_SESSION_STORAGE_KEYS = ['selectedCompanyId', 'companyTimezone'] as const;

export const clearClientSessionData = async (queryClient?: QueryClient): Promise<void> => {
    apiService.setCompanyId(null);
    apiService.setOrgId(null);

    await Promise.all([
        apiService.clearToken(),
        ...[...SESSION_STORAGE_KEYS, ...LEGACY_SESSION_STORAGE_KEYS].map((key) => removeStorageItem(key)),
    ]);

    if (typeof sessionStorage !== 'undefined') {
        sessionStorage.clear();
    }

    queryClient?.clear();
    // Wipe the persisted offline cache so the next user can't see prior data.
    await queryPersister.removeClient();
};

/**
 * One-time migration from the old two-key scheme (selectedCompanyId + companyTimezone)
 * to the new atomic `selectedCompanyData` key ({ id, timezone }).
 * Safe to call on every app start — it is a no-op when the new key already exists.
 */
export const migrateCompanyStorageIfNeeded = async (): Promise<void> => {
    try {
        const existing = await getStorageItem('selectedCompanyData');
        if (existing) return; // already migrated

        const [legacyId, legacyTz] = await Promise.all([
            getStorageItem('selectedCompanyId'),
            getStorageItem('companyTimezone'),
        ]);

        if (legacyId) {
            await setStorageItem(
                'selectedCompanyData',
                JSON.stringify({ id: legacyId, timezone: legacyTz || undefined }),
            );
        }

        await Promise.all([
            removeStorageItem('selectedCompanyId'),
            removeStorageItem('companyTimezone'),
        ]);
    } catch (error) {
        clientLogger.warn('[session] Company storage migration failed (non-fatal):', error);
    }
};